import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FiSave, FiUser, FiBriefcase, FiCreditCard, FiBook, FiUsers, FiChevronDown, FiPlus, FiLoader } from "react-icons/fi";
import Select from "react-select";
import axios from "axios";

// Unified Components & API
import SidebarNav from "../../components/MasterSideBar";
import TopbarNav from "../../components/TopbarNav";
import { updateStaff, getOneStaff } from "../../api/staff-calls-api";
import countries from "../../utils/countryList";

const countryOptions = countries.map((c) => ({ 
  label: `${c.flag} ${c.name}`, 
  value: c.name 
}));

const Section = ({ title, icon: Icon, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm transition-all mb-6">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-5 bg-white hover:bg-gray-50 transition-colors border-b border-gray-50"
      >
        <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg"><Icon size={18} /></div>
            <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest">{title}</h3>
        </div>
        <FiChevronDown className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white/50">{children}</div>}
    </div>
  );
};

const PMSettingsPage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const staffId = user?.id;
  const token = localStorage.getItem("token");

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({});

  const formatDate = (dateString) => (dateString ? new Date(dateString).toISOString().split("T")[0] : "");

  const fetchStaffData = useCallback(async () => {
    if (!staffId) return;
    try {
      const res = await getOneStaff(staffId);
      const data = res.data || res;
      setFormData({
        ...data,
        dateOfBirth: formatDate(data.dateOfBirth),
        dateOfEmployment: formatDate(data.dateOfEmployment),
        villageOrBlock: data.residence?.villageOrBlock || "",
        payam: data.residence?.payam || "",
        county: data.residence?.county || "",
        state: data.residence?.state || "",
        bankName: data.bankingDetails?.[0]?.bankName || "",
        bankBranch: data.bankingDetails?.[0]?.bankBranch || "",
        accountName: data.bankingDetails?.[0]?.accountName || "",
        accountNumber: data.bankingDetails?.[0]?.accountNumber || "",
        currency: data.bankingDetails?.[0]?.currency || "",
        swiftCode: data.bankingDetails?.[0]?.swiftCode || "",
        institution: data.qualifications?.[0]?.institution || "",
        award: data.qualifications?.[0]?.award || "",
        yearFrom: data.qualifications?.[0]?.yearFrom || "",
        yearTo: data.qualifications?.[0]?.yearTo || "",
        keyExpertise: data.workHistory?.keyExpertise || "",
        recentEmployer: data.workHistory?.recentEmployer || "",
        workrole: data.workHistory?.workrole || "",
        periodOfService: data.workHistory?.periodOfService || "",
        recentGrossAnnualPaymentRange: data.workHistory?.recentGrossAnnualPaymentRange || "",
        nokName: data.nextOfKin?.name || "",
        nokSex: data.nextOfKin?.sex || "",
        nokNationality: data.nextOfKin?.nationality || "",
        nokRelationship: data.nextOfKin?.relationship || "",
        nokVillageOrBlock: data.nextOfKin?.residence?.villageOrBlock || "",
        nokPayam: data.nextOfKin?.residence?.payam || "",
        nokCounty: data.nextOfKin?.residence?.county || "",
        nokState: data.nextOfKin?.residence?.state || "",
        nokTel: data.nextOfKin?.tel || "",
        nokMobile: data.nextOfKin?.mobile || "",
        nokEmail: data.nextOfKin?.email || "",
      });
    } catch (err) {
      console.log("Silent sync in progress..."); 
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { fetchStaffData(); }, [fetchStaffData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateStaff(staffId, formData);
      toast.success("Profile updated!");
    } catch (err) {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (label, name, type = "text", options, readOnly = false) => (
    <div className="space-y-1">
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">{label}</label>
      {options ? (
        <select name={name} value={formData[name] || ""} onChange={handleChange} className={`w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm font-bold ${readOnly ? "opacity-60" : ""}`} disabled={readOnly}>
          <option value="">-- Select {label} --</option>
          {options.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input name={name} type={type} value={formData[name] || ""} onChange={handleChange} className={`w-full bg-gray-50 border-none rounded-xl px-4 py-3 text-sm font-bold ${readOnly ? "opacity-60" : ""}`} readOnly={readOnly} placeholder={`Enter ${label}...`} />
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <ToastContainer theme="colored" />
      <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} onLogout={() => { localStorage.clear(); navigate("/"); }} />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 p-4 md:p-8 mt-16 lg:mt-20 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">Staff Settings</h1>
                <p className="text-gray-500 text-sm">Manage your profile and documentation.</p>
              </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Account Settings</h1>
              <p className="text-sm text-gray-500">Update your personal and professional profile</p>
            </div>
            </div>

            {loading ? (
                <div className="py-20 flex flex-col items-center justify-center bg-white rounded-3xl border border-gray-100 shadow-sm">
                  <FiLoader className="w-10 h-10 animate-spin text-orange-500 mb-2" />
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Syncing Records...</p>
                </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 pb-24">
                <Section title="Personal Information" icon={FiUser}>
                  {renderField("First Name", "firstName", "text", null, true)}
                  {renderField("Last Name", "lastName", "text", null, true)}
                  {renderField("Email", "email", "email", null, true)}
                  {renderField("Gender", "gender", "text", ["Male", "Female"], true)}
                  {renderField("Date of Birth", "dateOfBirth", "date", null, true)}
                  {renderField("Marital Status", "maritalStatus", "text", ["Single", "Married", "Other"])}
                  {renderField("National ID or Passport", "nationalIdOrPassport")}
                  {renderField("Village or Block", "villageOrBlock")}
                  {renderField("Payam", "payam")}
                  {renderField("County", "county")}
                  {renderField("State", "state")}
                  {renderField("Telephone", "tel")}
                  {renderField("Mobile", "mobile")}
                </Section>

                <Section title="Employment Details" icon={FiBriefcase}>
                  {renderField("Job Title", "jobTitle", "text", null, true)}
                  {renderField("Department", "department", "text", null, true)}
                  {renderField("Date of Employment", "dateOfEmployment", "date", null, true)}
                  {renderField("Employment Type", "employmentType", "text", null, true)}
                  {renderField("Contract Duration", "contractDuration", "text", null, true)}
                  {renderField("Salary Range", "salaryRange", "text", null, true)}
                </Section>

                <Section title="Banking Details" icon={FiCreditCard}>
                  {renderField("Bank Name", "bankName")}
                  {renderField("Bank Branch", "bankBranch")}
                  {renderField("Account Name", "accountName")}
                  {renderField("Account Number", "accountNumber")}
                  {renderField("Currency", "currency")}
                  {renderField("SWIFT Code", "swiftCode")}
                </Section>

                <Section title="Education & Work History" icon={FiBook}>
                  {renderField("Institution", "institution")}
                  {renderField("Award", "award")}
                  {renderField("Year From", "yearFrom")}
                  {renderField("Year To", "yearTo")}
                  {renderField("Key Expertise", "keyExpertise")}
                  {renderField("Recent Employer", "recentEmployer")}
                  {renderField("Role", "workrole")}
                  {renderField("Period of Service", "periodOfService")}
                </Section>

                <Section title="Next of Kin Information" icon={FiUsers}>
                  {renderField("Next of Kin Name", "nokName")}
                  {renderField("Relationship", "nokRelationship")}
                  {renderField("Village or Block", "nokVillageOrBlock")}
                  {renderField("Payam", "nokPayam")}
                  {renderField("County", "nokCounty")}
                  {renderField("State", "nokState")}
                  {renderField("Telephone", "nokTel")}
                  {renderField("Mobile", "nokMobile")}
                  {renderField("Email", "nokEmail", "email")}
                </Section>

                <div className="fixed bottom-8 right-8 z-40">
                    <button type="submit" disabled={saving} className="bg-orange-600 text-white px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-orange-700 shadow-2xl flex items-center gap-3 active:scale-95 disabled:opacity-50">
                        {saving ? <FiLoader className="animate-spin" /> : <FiSave size={18} />}
                        {saving ? "Saving Changes..." : "Save Profile"}
                    </button>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
      {/* Modal removed */}
    </div>
  );
};

export default PMSettingsPage;